/**
 * HALO - Profile Settings Section
 * User profile information management
 */

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { User, Mail, Building2, Shield, CheckCircle2, AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { supabase, updateUserProfile, UserRow } from "@/lib/supabase";

interface ProfileSectionProps {
  userId: string;
  onProfileUpdate?: (user: UserRow) => void;
}

export function ProfileSection({ userId, onProfileUpdate }: ProfileSectionProps) {
  const [user, setUser] = useState<UserRow | null>(null);
  const [fullName, setFullName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    loadUserProfile();
  }, [userId]);

  const loadUserProfile = async () => {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) throw error;

      const userData = data as UserRow;
      setUser(userData);
      setFullName(userData.full_name);
    } catch (err) {
      console.error("[ProfileSection] Error loading profile:", err);
      setMessage({ type: "error", text: "Failed to load profile" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!fullName.trim()) {
      setMessage({ type: "error", text: "Full name is required" });
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const result = await updateUserProfile(userId, { full_name: fullName.trim() });

      if (result.success && result.user) {
        setUser(result.user);
        onProfileUpdate?.(result.user);
        setMessage({ type: "success", text: "Profile updated successfully" });
      } else {
        setMessage({ type: "error", text: result.error || "Failed to update profile" });
      }
    } catch (err) {
      console.error("[ProfileSection] Error saving profile:", err);
      setMessage({ type: "error", text: "Failed to update profile" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-signal-crimson mx-auto mb-4" />
        <p className="text-muted-slate">Failed to load profile information</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-2xl font-bold text-primary">
              {user.full_name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{user.full_name}</h3>
            <p className="text-sm text-muted-slate">{user.email}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-glass bg-surface-dark text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              placeholder="Enter your full name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Email Address
              </label>
              <input
                type="email"
                value={user.email}
                disabled
                className="w-full px-4 py-2.5 rounded-xl border border-glass bg-surface-elevated text-muted-slate cursor-not-allowed"
              />
              <p className="text-xs text-muted-slate mt-1">
                Contact your administrator to change your email
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <Building2 className="w-4 h-4 inline mr-2" />
                Department
              </label>
              <input
                type="text"
                value={user.department || "Not assigned"}
                disabled
                className="w-full px-4 py-2.5 rounded-xl border border-glass bg-surface-elevated text-muted-slate cursor-not-allowed"
              />
              <p className="text-xs text-muted-slate mt-1">
                Contact your administrator to change your department
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <Shield className="w-4 h-4 inline mr-2" />
              Role
            </label>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-sm font-medium capitalize">
                {user.role}
              </span>
            </div>
          </div>
        </div>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-6 p-4 rounded-xl flex items-center gap-3 ${
              message.type === "success"
                ? "bg-signal-green/10 text-signal-green"
                : "bg-signal-crimson/10 text-signal-crimson"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="text-sm font-medium">{message.text}</span>
          </motion.div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving || fullName === user.full_name}
            className="px-6 py-2.5 bg-primary hover:bg-primary/90 disabled:bg-primary/50 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </Card>
    </motion.div>
  );
}
